import { useCallback, useState } from "react";
import { createCashDepositPaymentLink } from "../api/cash-deposit";
import { getWidgetApiError } from "../services/api";
import { APP_ROUTES } from "../constants";
import {
  appendAnalyticsHandoff,
  getAnalyticsSession,
  getEvents,
  safeCapture,
} from "../analytics";

/**
 * Opens the cash-deposit widget for an awaiting-funding operation by minting
 * an operation_payment magic link and doing a full navigation.
 */
export function useLaunchCashDepositPayment(linkId: string | null | undefined) {
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const launch = useCallback(
    async (operationId: string) => {
      if (!linkId || launching) return;
      setLaunching(true);
      setError(null);
      try {
        const callbackUrl = `${window.location.origin}${APP_ROUTES.HOME_PAGE}/?linkId=${linkId}`;
        const { url } = await createCashDepositPaymentLink(
          linkId,
          operationId,
          callbackUrl
        );
        const { sessionId, distinctId } = getAnalyticsSession();
        const handoffUrl =
          sessionId && distinctId
            ? appendAnalyticsHandoff(url, { sessionId, distinctId })
            : url;
        window.location.assign(handoffUrl);
      } catch (err) {
        console.error("[useLaunchCashDepositPayment] Failed to mint link:", err);
        safeCapture(getEvents().BARCODE_CREATED, {
          success: false,
          error_code: "CASH_DEPOSIT_LINK_FAILED",
          operation_id: operationId,
        });
        const maintenance = destinationMaintenanceCopy(err);
        setError(
          maintenance
            ? [maintenance.title, maintenance.body].filter(Boolean).join(" ")
            : "launchFailed",
        );
        setLaunching(false);
      }
    },
    [linkId, launching]
  );

  return { launch, launching, error, clearError: () => setError(null) };
}

function destinationMaintenanceCopy(
  error: unknown,
): { title: string; body: string } | null {
  const api = getWidgetApiError(error);
  if (api?.code !== "FLOW_UNAVAILABLE_MAINTENANCE") return null;
  const details = (api.details ?? {}) as { title?: string; body?: string };
  const title = details.title?.trim() ?? "";
  const body = details.body?.trim() ?? "";
  if (!title && !body) return null;
  return { title, body };
}
